"use client";

import React, { useState } from 'react';
import { Globe, Moon, Bell, Save, Loader2, Check } from 'lucide-react';
import { useLanguage } from '@/lib/language-context';
import { t } from '@/lib/translations';
import { useAppToast } from '@/lib/use-app-toast';
import { auditLog } from '@/lib/supabase/audit-logger';

interface NotificationPref {
  key: string;
  labelKey: keyof typeof import('@/lib/translations').translations.en;
  description: string;
  enabled: boolean;
  channel: 'email' | 'sms' | 'both';
}

export default function PreferencesSettings() {
  const { language, setLanguage } = useLanguage();
  const [isSaving, setIsSaving] = useState(false);
  const [savedLang, setSavedLang] = useState(language);
  const { success } = useAppToast();

  const [notifications, setNotifications] = useState<NotificationPref[]>([
    {
      key: 'critical_threats',
      labelKey: 'settings_notif_threats',
      description: 'Immediate alert for Critical and High severity threats',
      enabled: true,
      channel: 'both',
    },
    {
      key: 'compliance_warnings',
      labelKey: 'settings_notif_compliance',
      description: 'BD Compliance Act 2023 section status changes',
      enabled: true,
      channel: 'email',
    },
    {
      key: 'login_activity',
      labelKey: 'settings_notif_login',
      description: 'Alert on every successful or failed login attempt',
      enabled: true,
      channel: 'email',
    },
    {
      key: 'weekly_reports',
      labelKey: 'settings_notif_report',
      description: 'Weekly security posture summary every Monday 08:00 BDT',
      enabled: false,
      channel: 'email',
    },
  ]);

  const toggleNotif = (key: string) => {
    setNotifications(prev =>
      prev.map(n => n.key === key ? { ...n, enabled: !n.enabled } : n)
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    // Backend integration: await supabase.from('user_preferences').update({ language, notifications })
    await new Promise(r => setTimeout(r, 1000));
    setSavedLang(language);
    await auditLog.preferencesUpdated();
    setIsSaving(false);
    success(t(language, 'settings_saved'), 'Preferences updated. Language change takes effect immediately.');
  };

  return (
    <div className="space-y-5">
      {/* Language */}
      <div className="cyber-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Globe size={15} className="text-cyber-blue" />
          <h3 className={`text-sm font-semibold text-white ${language === 'bn' ? 'font-bangla' : ''}`}>
            {t(language, 'settings_language')}
          </h3>
        </div>

        <p className={`text-xs text-cyber-muted mb-4 ${language === 'bn' ? 'font-bangla' : ''}`}>
          {t(language, 'settings_language_desc')}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { value: 'en', label: t(language, 'settings_english'), sublabel: 'English', flag: '🇬🇧' },
            { value: 'bn', label: t(language, 'settings_bangla'), sublabel: 'বাংলা', flag: '🇧🇩' },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => setLanguage(opt.value as 'en' | 'bn')}
              className={`
                flex items-center gap-3 px-4 py-3 rounded-xl border text-left
                transition-all duration-150
                ${language === opt.value
                  ? 'bg-cyber-blue/10 border-cyber-blue/35 shadow-[0_0_15px_rgba(0,163,255,0.08)]'
                  : 'bg-[#131620] border-cyber-border hover:border-cyber-blue/25'
                }
              `}
            >
              <span className="text-2xl">{opt.flag}</span>
              <div className="flex-1">
                <p className={`text-sm font-medium text-white ${opt.value === 'bn' ? 'font-bangla' : ''}`}>
                  {opt.label}
                </p>
                <p className={`text-[11px] text-cyber-muted ${opt.value === 'bn' ? 'font-bangla' : 'font-mono'}`}>
                  {opt.sublabel}
                </p>
              </div>
              {language === opt.value && (
                <div className="w-5 h-5 rounded-full bg-cyber-blue flex items-center justify-center">
                  <Check size={11} className="text-white" />
                </div>
              )}
            </button>
          ))}
        </div>

        {language !== savedLang && (
          <div className="mt-3 px-3 py-2 bg-yellow-500/10 border border-yellow-500/25 rounded-lg flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full" />
            <p className="text-[11px] text-yellow-400 font-mono">
              Language changed — save to persist across sessions
            </p>
          </div>
        )}
      </div>

      {/* Theme */}
      <div className="cyber-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Moon size={15} className="text-cyber-purple" />
          <h3 className={`text-sm font-semibold text-white ${language === 'bn' ? 'font-bangla' : ''}`}>
            {t(language, 'settings_theme')}
          </h3>
        </div>
        <p className={`text-xs text-cyber-muted mb-4 ${language === 'bn' ? 'font-bangla' : ''}`}>
          {t(language, 'settings_theme_desc')}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl border bg-cyber-blue/10 border-cyber-blue/35 cursor-pointer">
            <div className="w-10 h-8 rounded-lg bg-[#0D0D0D] border border-cyber-border flex items-center justify-center">
              <div className="w-4 h-1 bg-cyber-blue rounded-full" />
            </div>
            <div className="flex-1">
              <p className={`text-sm font-medium text-white ${language === 'bn' ? 'font-bangla' : ''}`}>
                {t(language, 'settings_deep_space')}
              </p>
              <p className="text-[10px] text-cyber-muted font-mono">Obsidian · Cyber Blue</p>
            </div>
            <div className="w-5 h-5 rounded-full bg-cyber-blue flex items-center justify-center">
              <Check size={11} className="text-white" />
            </div>
          </div>

          <div className="flex items-center gap-3 px-4 py-3 rounded-xl border bg-[#131620] border-cyber-border cursor-pointer hover:border-cyber-blue/25 transition-colors opacity-60">
            <div className="w-10 h-8 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center">
              <div className="w-4 h-1 bg-blue-600 rounded-full" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-white">Light Mode</p>
              <p className="text-[10px] text-cyber-muted font-mono">Coming soon</p>
            </div>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="cyber-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Bell size={15} className="text-cyber-yellow" />
          <h3 className={`text-sm font-semibold text-white ${language === 'bn' ? 'font-bangla' : ''}`}>
            {t(language, 'settings_notifications')}
          </h3>
        </div>

        <div className="space-y-3">
          {notifications.map((notif) => (
            <div
              key={notif.key}
              className="flex items-start justify-between gap-4 py-3 border-b border-cyber-border/50 last:border-0"
            >
              <div className="flex-1">
                <p className={`text-sm text-cyber-text font-medium ${language === 'bn' ? 'font-bangla' : ''}`}>
                  {t(language, notif.labelKey)}
                </p>
                <p className="text-[11px] text-cyber-muted mt-0.5">{notif.description}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <select
                    value={notif.channel}
                    onChange={(e) => {
                      setNotifications(prev =>
                        prev.map(n => n.key === notif.key ? { ...n, channel: e.target.value as 'email' | 'sms' | 'both' } : n)
                      );
                    }}
                    className="bg-[#131620] border border-cyber-border rounded px-2 py-0.5 text-[10px] text-cyber-muted font-mono outline-none cursor-pointer"
                    disabled={!notif.enabled}
                  >
                    <option value="email">Email only</option>
                    <option value="sms">SMS only</option>
                    <option value="both">Email + SMS</option>
                  </select>
                </div>
              </div>
              <button
                onClick={() => toggleNotif(notif.key)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 shrink-0 mt-0.5 ${
                  notif.enabled ? 'bg-cyber-blue' : 'bg-cyber-border'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                  notif.enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={`
            flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium
            transition-all duration-150 active:scale-95
            ${isSaving
              ? 'bg-cyber-surface border border-cyber-border text-cyber-subtle cursor-not-allowed' :'bg-cyber-blue hover:bg-cyber-blue/80 text-white shadow-[0_0_15px_rgba(0,163,255,0.25)]'
            }
          `}
          style={{ minWidth: '130px' }}
        >
          {isSaving ? (
            <><Loader2 size={14} className="animate-spin" /><span>Saving...</span></>
          ) : (
            <><Save size={14} /><span className={language === 'bn' ? 'font-bangla' : ''}>{t(language, 'settings_save')}</span></>
          )}
        </button>
      </div>
    </div>
  );
}